import { CodeIcon, TerminalWindowIcon } from '@phosphor-icons/react';
import { Badge } from '~/components/ui/badge';
import { InlineMarkdown } from '~/components/inline-markdown';
import { PopoverContent } from '~/components/ui/popover';
import { cn } from '~/lib/utils';
import { GroupLabel, StateRing } from '../activity-board';
import { BuildingTag } from '../building-tag';
import {
  ATTENTION_ORDER,
  actionVerbFor,
  boardRowLabel,
  formatBarTime,
  statusLineFor,
  waitingLineFor,
  type BarChat,
  type BarChatState,
  type BarGroup,
} from '../state-mapping';
import { STATE_COLOR_CLASS, STATE_PILL_LABEL } from '../state-style';
import { NewChatButtonLegacy } from './new-chat-button-legacy';
import { actionChipClass, STATE_PILL_CLASS } from './state-style-legacy';

/**
 * FRONT-3223: the activity board as on `origin/main`, for the
 * `eddy.ui-refactoring` OFF path. What the reworked board (`../activity-board.tsx`)
 * dropped and this one keeps: the "Chat activity · live" header with one
 * count pill per state, the "New chat / Open all chats →" footer, and the
 * amber-tinted hover action chip on an asking row. The ring and the group
 * heading are unchanged and shared.
 */

/** A board row — identical to the reworked one except for `actionChipClass`. */
function BoardRowLegacy({
  chat,
  onOpen,
  isCurrent,
}: {
  chat: BarChat;
  onOpen: (sessionId: string, title: string | null) => void;
  isCurrent?: boolean;
}) {
  const time = formatBarTime(chat.lastActiveAt);
  const action = actionVerbFor(chat);
  const waitingLine = waitingLineFor(chat);
  return (
    <button
      type="button"
      onClick={() => onOpen(chat.sessionId, chat.title)}
      // FRONT-1429: a fixed accessible name, so a row's name does not change
      // as the chat progresses and the clock moves.
      aria-label={boardRowLabel(chat)}
      data-testid={chat.kind === 'pro-builder' ? 'board-row-pro-builder' : undefined}
      aria-current={isCurrent ? 'true' : undefined}
      className={cn(
        'group flex w-full items-center gap-[11px] rounded-[10px] px-[12px] py-[9px] text-left transition-colors hover:bg-[var(--bg-3)]',
        isCurrent && 'border border-primary/40 bg-primary/10 hover:bg-primary/15',
      )}
    >
      <StateRing state={chat.state}>
        {chat.kind === 'pro-builder' ? (
          <TerminalWindowIcon
            size={12}
            weight="bold"
            aria-hidden
            className={cn(STATE_COLOR_CLASS[chat.state], chat.state === 'asking' && 'eddy-tb-blink')}
          />
        ) : chat.kind === 'build' ? (
          <CodeIcon
            size={12}
            weight="bold"
            className={cn(STATE_COLOR_CLASS[chat.state], chat.state === 'asking' && 'eddy-tb-blink')}
          />
        ) : (
          <span
            className={cn(
              'size-[7px] rounded-full bg-current',
              STATE_COLOR_CLASS[chat.state],
              chat.state === 'asking' && 'eddy-tb-blink',
            )}
          />
        )}
      </StateRing>
      <span className="min-w-0 flex-1">
        <span className="flex min-w-0 items-center gap-[6px]">
          <span className="min-w-0 truncate text-[14.5px] font-medium text-[var(--ink-0)]">{chat.title}</span>
          {chat.directAgent && (
            <Badge variant="outline" data-testid="chat-row-direct-agent" className="shrink-0 max-w-[40%] py-0 text-[10px]">
              <span className="truncate">Direct · {chat.directAgent.name}</span>
            </Badge>
          )}
          {chat.kind === 'pro-builder' &&
            (chat.state === 'running' ? (
              <BuildingTag />
            ) : (
              <Badge variant="outline" className="eddy-tb-kind-tag shrink-0 py-0 text-[10px]">
                Claude Code
              </Badge>
            ))}
          {chat.kind === 'build' &&
            // Two tags, never both: the live "$ building" chip while the
            // session is building, else the quiet kind marker.
            (chat.state === 'running' ? (
              <BuildingTag />
            ) : (
              <Badge variant="outline" className="eddy-tb-kind-tag shrink-0 py-0 text-[10px]">
                Coding session
              </Badge>
            ))}
        </span>
        <span className="mt-px block truncate text-[13px] text-[var(--ink-2)]">
          {waitingLine ? (
            <>
              {waitingLine.prefix} <InlineMarkdown as="span" text={waitingLine.text} />
            </>
          ) : (
            statusLineFor(chat)
          )}
        </span>
      </span>
      <span className="relative shrink-0">
        <span
          className={cn(
            'hidden items-center rounded-[7px] px-[9px] py-[4px] text-xs font-medium group-hover:inline-flex',
            actionChipClass(chat.state),
          )}
        >
          {action}
        </span>
        {time && <span className="font-mono text-xs text-[var(--ink-3)] group-hover:hidden">{time}</span>}
      </span>
    </button>
  );
}

interface ActivityBoardLegacyProps {
  groups: BarGroup[];
  onOpen: (sessionId: string, title: string | null) => void;
  /** FRONT-1681: opens a Building Session row — a build session id is not a
   *  chat session id. A build row with no handler is inert on click. */
  onOpenBuild?: (sessionId: string, title: string | null) => void;
  /** FRONT-3040: opens the Pro Builder window from its row. */
  onOpenProBuilder?: () => void;
  /** The footer's "New chat" button; absent, no button renders. */
  onNew?: () => void;
  /** The footer's "Open all chats →" link; absent, no link renders. */
  onAllChats?: () => void;
  /** FRONT-1579: the chat the caller is currently sitting in, marked. */
  currentSessionId?: string;
}

/** One stable reference for a row kind with nothing wired. */
function noopOpen(): void {}

/**
 * The dropdown that drops from the status cluster — the full recent-chat
 * list, grouped by state. Renders as the `Popover`'s content, so it must be
 * mounted inside the same `<Popover>` as the status cluster.
 */
export function ActivityBoardLegacy({
  groups,
  onOpen,
  onOpenBuild,
  onOpenProBuilder,
  onNew,
  onAllChats,
  currentSessionId,
}: ActivityBoardLegacyProps) {
  const countFor = (state: BarChatState) => groups.find((g) => g.state === state)?.items.length ?? 0;
  const openHandlerFor = (kind: string) => {
    if (kind === 'pro-builder') return onOpenProBuilder ?? noopOpen;
    if (kind === 'build') return onOpenBuild ?? noopOpen;
    return onOpen;
  };

  return (
    <PopoverContent
      align="start"
      sideOffset={8}
      aria-label="Chat activity"
      className="eddy-topbar eddy-tb-enter flex max-h-[calc(100vh-96px)] w-[440px] max-w-[calc(100vw-32px)] flex-col gap-0 overflow-hidden rounded-[16px] border border-[var(--line)] bg-[var(--bg-1)] p-0 shadow-lg"
    >
      <div className="shrink-0 border-b border-[var(--line)] bg-[var(--bg-2)] px-[16px] pt-[13px] pb-[11px]">
        <div className="flex items-center gap-[10px]">
          <span className="flex-1 text-[16px] font-semibold text-[var(--ink-0)]">Chat activity</span>
          <span className="font-mono text-xs text-[var(--ink-3)]">live</span>
          <span className="eddy-tb-pulse size-[7px] shrink-0 rounded-full bg-[var(--eddy-blue)]" aria-hidden="true" />
        </div>
        <div className="mt-[10px] flex flex-wrap gap-[6px]">
          {ATTENTION_ORDER.map((state) => {
            const count = countFor(state);
            if (count === 0) return null;
            return (
              <span
                key={state}
                className={cn('inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium', STATE_PILL_CLASS[state])}
              >
                {count} {STATE_PILL_LABEL[state]}
              </span>
            );
          })}
        </div>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto px-[6px] pt-[4px] pb-[6px]">
        {groups.length ? (
          groups.map((g) => (
            <div key={g.state}>
              <GroupLabel state={g.state} count={g.items.length} />
              {g.items.map((c) => (
                <BoardRowLegacy
                  // Chat and build session ids come from two tables, so the
                  // bare sessionId alone is not unique within this list.
                  key={`${c.kind}:${c.sessionId}`}
                  chat={c}
                  onOpen={openHandlerFor(c.kind)}
                  // Only a chat row can be "the chat you're currently in".
                  isCurrent={c.kind === 'chat' && c.sessionId === currentSessionId}
                />
              ))}
            </div>
          ))
        ) : (
          <div className="px-[16px] py-[26px] text-center text-[13px] text-[var(--ink-2)]">
            Nothing running. Start a chat and it shows up here.
          </div>
        )}
      </div>

      <div className="flex shrink-0 items-center gap-[10px] border-t border-[var(--line)] px-[12px] py-[10px]">
        {onNew && <NewChatButtonLegacy size="sm" onClick={onNew} />}
        <span className="flex-1" />
        {onAllChats && (
          <button
            type="button"
            onClick={onAllChats}
            className="text-[13px] font-medium text-[var(--eddy-blue-text)] hover:underline"
          >
            Open all chats →
          </button>
        )}
      </div>
    </PopoverContent>
  );
}
